import { createRoute, redirect } from "@tanstack/react-router";
import { useAuthStore } from "@/lib/Auth/authStore";
import { organisationsQueryOptions } from "@/lib/Auth/organisationsQuery";
import { QueryClient } from "@/lib/ReactQuery/QueryClient";
import { rootRoute } from "@/routes/root-route";
import { clearLastScope, readLastScope } from "@/routes/lastScope";

/**
 * Bare `/` (and any external entry without a scope). If we remembered the org/
 * supplier the user was last viewing — and they're still a member — we send them
 * straight back into the scoped tree. Otherwise (no memory, or stale org) we
 * fall back to the org picker, which routes them into the scoped tree once they
 * choose. Unauthenticated users go to sign-in.
 */
export const rootIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: async () => {
    const token = useAuthStore.getState().token;
    if (!token) {
      throw redirect({ to: "/sign-in" });
    }

    const lastScope = readLastScope();
    if (lastScope) {
      // Confirm the remembered org is still a membership before redirecting, so
      // a removed/transferred org can't bounce the user index → scope → picker.
      const orgData = await QueryClient.ensureQueryData(
        organisationsQueryOptions(token),
      ).catch(() => null);
      const stillMember = (orgData?.organisations ?? []).some(
        (org) => org.id === lastScope.orgId,
      );

      if (stillMember) {
        throw redirect({
          to: "/$tenant/$orgId/supplier/{-$supplierId}",
          params: {
            tenant: lastScope.tenant,
            orgId: lastScope.orgId,
            supplierId: lastScope.supplierId,
          },
        });
      }

      clearLastScope();
    }

    throw redirect({ to: "/org" });
  },
});
